import axios from 'axios';
import {
  API_USERS,
  API_USERS_CONFIGURATION,
  API_USERS_SINGUP,
  API_USERS_GOOGLE,
  API_USERS_LOGIN,
  API_USERS_LOGIN_GOOGLE,
  API_USERS_FORGOT,
  API_USERS_PIN_CODE,
  API_USERS_AZURE
} from '../../services/api/apiConst';
import { manageObjectResponse } from './apiResponse';
import { PARAMS } from '../../const';
import { API_USERS_RESET } from './apiConst';
import { getLogger } from '../logger';
import { API_USERS_TASKS_EVALUATIONS } from './apiConstV2';

const debug = getLogger('services:api:userApi');

/**
 *
 * @param {string} userId
 * @return {Promise<UserResponse>}
 */
export async function getUser(userId) {
  if (!userId) {
    debug.error('[getUser] user id is empty');
    return undefined;
  }

  const response = await axios.get(`${API_USERS}/${userId}`);

  response.data.createdAt = new Date(response.data.createdAt);
  response.data.updateAt = new Date(response.data.updateAt);

  return response;
}

export let signUpWithGoogle = user => {
  if (user) {
    return manageObjectResponse(axios.post(API_USERS_GOOGLE, user, { preventInterceptors: true }));
  }
  debug.error('[singUpWithGoogle] user is empty');
};

export let signUpWithAzure = user => {
  if (user) {
    return manageObjectResponse(axios.post(API_USERS_AZURE, user, { preventInterceptors: true }));
  }
  debug.error('[singUpWithGoogle] user is empty');
};

export let login = (email, password, alternativeUrl) => {
  if (email && password) {
    const config = {
      url: alternativeUrl ? alternativeUrl : `${API_USERS_LOGIN}`,
      method: 'post',
      data: { email, password }
    };
    return manageObjectResponse(axios(config));
  }

  debug.error('[login] email or password is empty. email: "%s" | password: "%s"', email, password);
};

export let loginGoogle = (email, accessToken) => {
  if (email && accessToken) {
    let payload = { email: email, accessToken: accessToken };
    return manageObjectResponse(axios.post(`${API_USERS_LOGIN_GOOGLE}`, payload));
  }
  debug.error('[loginGoogle] email(%s) or accessToken(%s) is empty', email, accessToken);
};

export let signUp = user => {
  if (user) {
    return manageObjectResponse(axios.post(`${API_USERS_SINGUP}`, user));
  }
  debug.error('[signUp] user is empty');
};

export let requestNewPassword = email => {
  if (email) {
    return manageObjectResponse(axios.get(`${API_USERS_FORGOT.replace(PARAMS.EMAIL, email)}`));
  }
  debug.error('[requestNewPassword] email is empty');
};

export let resetPassword = (newPassword, token) => {
  if (newPassword && token) {
    return manageObjectResponse(
      axios.put(`${API_USERS_RESET.replace(PARAMS.TOKEN, token)}`, { newPassword: newPassword })
    );
  }
  debug.error('[resetPassword] newPassword: "%s" or token: "%s" is empty', newPassword, token);
};

export let updateUserData = (userId, user) => {
  if (userId && user) {
    return manageObjectResponse(axios.put(`${API_USERS}/${userId}`, user));
  }
  debug.error('[updateUserData] userId: %s or user: %O is empty', userId, user);
};

export let updateUserPicture = (userId, pictureData) => {
  if (userId) {
    return manageObjectResponse(axios.put(`${API_USERS}/${userId}/picture`, pictureData));
  }
  debug.error('[updateUserData] userId: %s or picture: %O is empty', userId, pictureData);
};

export let createEvaluation = (classroomId, userId, taskId, evaluation) => {
  if (userId && taskId) {
    const url = API_USERS_TASKS_EVALUATIONS.replace(PARAMS.CLASSROOM, classroomId)
      .replace(PARAMS.USER, userId)
      .replace(PARAMS.TASK, taskId);
    return manageObjectResponse(axios.post(url, evaluation));
  }
  debug.error('[evaluateUserTask] userId: %s or taskId: %s is empty', userId, taskId);
};

export let updateEvaluation = (classroomId, userId, taskId, evaluation) => {
  if (userId && taskId) {
    const url = API_USERS_TASKS_EVALUATIONS.replace(PARAMS.CLASSROOM, classroomId)
      .replace(PARAMS.USER, userId)
      .replace(PARAMS.TASK, taskId);
    return manageObjectResponse(axios.patch(url, evaluation));
  }
  debug.error('[evaluateUserTask] userId: %s or taskId: %s is empty', userId, taskId);
};

export let deleteUserData = userId => {
  if (userId) {
    return manageObjectResponse(axios.delete(`${API_USERS}/${userId}`));
  }
  debug.error('[deleteUserData] userId is empty');
};

export function getStudentPinCode(studentId, force) {
  if (studentId) {
    let url = API_USERS_PIN_CODE.replace(PARAMS.USER, studentId);
    url = force ? `${url}?force=true` : url;

    return manageObjectResponse(
      axios.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'file-format': 'arraybuffer'
        }
      })
    );
  }
  debug.error('[getStudentPinCode] classroom section id is empty');
}

export function getUserConfiguration(userId) {
  if (userId) {
    let url = API_USERS_CONFIGURATION.replace(PARAMS.USER, userId);
    return axios.get(url).then(response => {
      return manageObjectResponse(response.data);
    });
  }
  debug.error('[getUserConfiguration]');
}

export function updateUserConfiguration(userId, configuration) {
  if (userId) {
    let url = API_USERS_CONFIGURATION.replace(PARAMS.USER, userId);
    return axios.put(url, configuration).then(response => {
      return manageObjectResponse(response.data);
    });
  }
  debug.error('[updateUserConfiguration]');
}

/**
 * Use info
 * @typedef {object} User
 * @property {string} _id
 * @property {string} name
 * @property {string} surname
 * @property {string} email
 * @property {string} userId
 * @property {Date} createdAt
 * @property {Date} updateAt
 * @property {string} picture
 * @property {string} language
 * @property {string} role
 * @property {string} institutionId
 * @property {0 | 1} enabled
 */

/**
 * User info response
 * @typedef {object} UserResponse
 * @property {User} data
 * @property {number} status
 */
